// src/pages/Services.tsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Filter, Star, Clock, Package, Sparkles } from 'lucide-react';
import ServiceCard from '../components/ui/ServiceCard';
import { services, serviceCategories } from '../data/services';
import { staggerContainer, fadeInUp } from '../utils/animations';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Local function to format prices in Ghana Cedis
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const Services: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'price' | 'name' | 'popular'>('popular');

  const filteredServices = services
    .filter(service => activeCategory === 'all' || service.category === activeCategory)
    .sort((a, b) => {
      if (sortBy === 'price') return a.price - b.price;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return a.popular === b.popular ? 0 : a.popular ? -1 : 1;
    });

  const downloadPriceList = () => {
    // Create a formatted price list
    const content = `GET NAILED BY AJ - PRICE LIST\n${new Date().toLocaleDateString()}\n\n` +
      services.map(s => `${s.name}: ${formatPrice(s.price)} (${s.duration})`).join('\n') +
      '\n\n📍 Westlands Blvd, Hatso, Accra\n📞 0594197765\n📧 gtnailedbyaj@gmail.com\n\n' +
      'Follow us on social media:\nInstagram: @getnailedbyaj\nTikTok: @getnailedbyaj\nWhatsApp: 0594197765';
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `get-nailed-by-aj-price-list-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  // Calculate service statistics
  const totalServices = services.length;
  const averagePrice = Math.round(services.reduce((acc, s) => acc + s.price, 0) / services.length);
  const popularCount = services.filter(s => s.popular).length;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-mauve-50 to-white pt-24 pb-16">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center gap-2 mb-6">
              <Star className="text-mauve-500" size={24} />
              <span className="text-mauve-600 font-medium uppercase tracking-wider text-sm">
                Premium Beauty Studio
              </span>
              <Star className="text-mauve-500" size={24} />
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-mauve-900 mb-6 leading-tight">
              Complete Beauty Services
              <span className="block text-gradient-mauve text-4xl md:text-5xl mt-2">
                in Accra
              </span>
            </h1>
            
            <p className="text-xl text-mauve-700 mb-8 max-w-2xl mx-auto">
              Professional nails, eyelashes, and makeup services with authentic Ghanaian ingredients 
              and international standards
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={downloadPriceList}
                className="btn-primary inline-flex items-center group"
              >
                <Download size={20} className="mr-2 group-hover:animate-bounce" />
                Download Price List (₵)
              </button>
              
              <a 
                href="https://wa.me/233594197765?text=Hello%20GetNailedbyAJ!%20I'd%20like%20to%20book%20a%20service."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline inline-flex items-center"
              >
                💬 WhatsApp for Booking
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Statistics */}
      <section className="py-12 bg-gradient-to-r from-white to-mauve-50">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-mauve-900 mb-2">{totalServices}</div>
              <div className="text-mauve-600 text-sm">Total Services</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-mauve-900 mb-2">{formatPrice(averagePrice)}</div>
              <div className="text-mauve-600 text-sm">Average Price</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-mauve-900 mb-2">{popularCount}</div>
              <div className="text-mauve-600 text-sm">Popular Services</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-mauve-900 mb-2">{serviceCategories.length}</div>
              <div className="text-mauve-600 text-sm">Categories</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={ref} className="section-padding">
        <motion.div
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="container-custom"
        >
          {/* Controls Header */}
          <motion.div 
            variants={fadeInUp}
            className="bg-gradient-to-br from-white to-mauve-50 rounded-2xl shadow-lg p-6 md:p-8 mb-12 border border-mauve-100"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              {/* Category Filters */}
              <div className="flex-1">
                <div className="flex items-center text-mauve-700 mb-4">
                  <Filter size={20} className="mr-3" />
                  <span className="font-bold text-lg">Filter Services</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${
                      activeCategory === 'all'
                        ? 'bg-gradient-to-r from-mauve-500 to-mauve-600 text-white shadow-lg shadow-mauve-300/50'
                        : 'bg-mauve-100 text-mauve-700 hover:bg-mauve-200 hover:shadow-md'
                    }`}
                    onClick={() => setActiveCategory('all')}
                  >
                    <Sparkles size={14} className="mr-2" />
                    All Services
                  </button>
                  {serviceCategories.map(category => (
                    <button
                      key={category.id}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        activeCategory === category.id
                          ? `bg-gradient-to-r ${category.id === 'nails' ? 'from-pink-500 to-rose-500' : 
                            category.id === 'eyelashes' ? 'from-purple-500 to-indigo-500' : 
                            category.id === 'makeup' ? 'from-rose-500 to-pink-500' : 
                            'from-indigo-500 to-blue-500'} text-white shadow-lg`
                          : `bg-mauve-100 text-mauve-700 hover:bg-mauve-200 hover:shadow-md`
                      }`}
                      onClick={() => setActiveCategory(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Options */}
              <div>
                <div className="flex items-center text-mauve-700 mb-4">
                  <Clock size={20} className="mr-3" />
                  <span className="font-bold text-lg">Sort By</span>
                </div>
                <div className="flex gap-2">
                  {[
                    { id: 'popular', label: 'Popular', icon: Star },
                    { id: 'price', label: 'Price', icon: Download },
                    { id: 'name', label: 'Name', icon: Package }
                  ].map((option) => (
                    <button
                      key={option.id}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${
                        sortBy === option.id
                          ? 'bg-mauve-500 text-white shadow-lg shadow-mauve-300/50'
                          : 'bg-mauve-100 text-mauve-700 hover:bg-mauve-200 hover:shadow-md'
                      }`}
                      onClick={() => setSortBy(option.id as any)}
                    >
                      <option.icon size={14} className="mr-2" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Active filters info */}
            <div className="mt-6 pt-6 border-t border-mauve-200">
              <div className="flex items-center justify-between">
                <div className="text-mauve-600 text-sm">
                  Showing <span className="font-bold text-mauve-900">{filteredServices.length}</span> of{' '}
                  <span className="font-bold text-mauve-900">{services.length}</span> services
                </div>
                {activeCategory !== 'all' && (
                  <button
                    onClick={() => setActiveCategory('all')}
                    className="text-sm text-mauve-500 hover:text-mauve-700 hover:underline"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Services Grid */}
          {filteredServices.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  custom={index}
                >
                  <ServiceCard service={service} />
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              variants={fadeInUp}
              className="text-center py-16 bg-gradient-to-br from-mauve-50 to-white rounded-2xl border border-mauve-200"
            >
              <Package size={64} className="text-mauve-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-mauve-800 mb-2">No Services Found</h3>
              <p className="text-mauve-600 mb-6">Try selecting a different category</p>
              <button
                onClick={() => setActiveCategory('all')}
                className="btn-outline"
              >
                Show All Services
              </button>
            </motion.div>
          )}

          {/* Service Information */}
          <motion.div 
            variants={fadeInUp}
            className="mt-16 bg-gradient-to-br from-white to-mauve-50 rounded-2xl p-8 border border-mauve-200 shadow-lg"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-mauve-900 mb-2">
                Why Choose GetNailedbyAJ
              </h3>
              <p className="text-mauve-600">Premium beauty services with a Ghanaian touch</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🇬🇭</span>
                </div>
                <h4 className="font-bold text-mauve-800 mb-3">Ghanaian Ingredients</h4>
                <ul className="space-y-2 text-mauve-700 text-sm">
                  <li>Authentic shea butter from Northern Ghana</li>
                  <li>Local neem leaves & coconut oil</li>
                  <li>Baobab oil for premium care</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl"></span>
                </div>
                <h4 className="font-bold text-mauve-800 mb-3">Certified Experts</h4>
                <ul className="space-y-2 text-mauve-700 text-sm">
                  <li>Internationally trained technicians</li>
                  <li>Continuous professional development</li>
                  <li>5+ years industry experience</li>
                </ul>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-mauve-100 to-mauve-50 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl"></span>
                </div>
                <h4 className="font-bold text-mauve-800 mb-3">Flexible Services</h4>
                <ul className="space-y-2 text-mauve-700 text-sm">
                  <li>Mobile services across Greater Accra</li>
                  <li>Weekend & evening appointments</li>
                  <li>Group packages available</li>
                </ul>
              </div>
            </div>
            
            {/* Booking CTA */}
            <div className="mt-12 pt-8 border-t border-mauve-200">
              <div className="text-center">
                <h4 className="text-xl font-bold text-mauve-900 mb-4">Ready to Book?</h4>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="tel:+233594197765"
                    className="btn-primary inline-flex items-center justify-center"
                  >
                     Call Now: 0594197765
                  </a>
                  <a 
                    href="https://wa.me/233594197765"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline inline-flex items-center justify-center"
                  >
                    WhatsApp Booking
                  </a>
                </div>
                <p className="text-sm text-mauve-600 mt-4">
                  Open 7 days a week • 9AM - 7PM • Westlands Blvd, Hatso, Accra
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>
    </motion.main>
  );
};

export default Services;